import { logger } from "~/lib/logger";
import { queryClient, trpc } from "~/lib/trpc";
import { dexie } from "./db";
import { useConfigStore } from "./store";
import type { Brand, ColumnInDB, Option, OptionGroup, UiField, UiTable } from "./types";

const CACHE_KEY = "ui-config";
const CACHE_VERSION = 1;

let bootPromise: Promise<void> | null = null;
let refreshPromise: Promise<void> | null = null;

function fetchBootstrapData() {
  return queryClient.fetchQuery({
    ...trpc.crm.getDexieBootstrapData.queryOptions(),
    staleTime: 0,
  });
}

type BootstrapData = Awaited<ReturnType<typeof fetchBootstrapData>>;

async function replaceDexieData({ brands, options, optionGroups, uiTables, uiFields, columnsInDB }: BootstrapData) {
  await dexie.transaction(
    "rw",
    [
      dexie.brands,
      dexie.options,
      dexie.optionGroups,
      dexie.uiTables,
      dexie.uiFields,
      dexie.columnsInDB,
      dexie.cacheMetadata,
    ],
    async () => {
      await dexie.brands.clear();
      await dexie.options.clear();
      await dexie.optionGroups.clear();
      await dexie.uiTables.clear();
      await dexie.uiFields.clear();
      await dexie.columnsInDB.clear();

      if (brands.length) await dexie.brands.bulkPut(brands as Brand[]);
      if (options.length) await dexie.options.bulkPut(options as Option[]);
      if (optionGroups.length) await dexie.optionGroups.bulkPut(optionGroups as OptionGroup[]);
      if (uiTables.length) await dexie.uiTables.bulkPut(uiTables as UiTable[]);
      if (uiFields.length) await dexie.uiFields.bulkPut(uiFields as UiField[]);
      if (columnsInDB.length) await dexie.columnsInDB.bulkPut(columnsInDB as ColumnInDB[]);

      await dexie.cacheMetadata.put({
        key: CACHE_KEY,
        cacheVersion: CACHE_VERSION,
        updatedAt: new Date().toISOString(),
      });
    },
  );

  await useConfigStore.getState().loadConfigsFromDexie();
}

function startRefresh() {
  const promise = fetchBootstrapData().then(replaceDexieData);
  refreshPromise = promise;
  void promise.then(
    () => {
      if (refreshPromise === promise) {
        refreshPromise = null;
      }
    },
    () => {
      if (refreshPromise === promise) {
        refreshPromise = null;
      }
    },
  );
  return promise;
}

export async function refreshDexieConfig({ force = false }: { force?: boolean } = {}) {
  if (refreshPromise) {
    if (!force) return refreshPromise;

    try {
      await refreshPromise;
    } catch {
      // The forced refresh below retries after a failed in-flight request.
    }
    return refreshDexieConfig();
  }

  return startRefresh();
}

export function ensureDexieConfig() {
  if (useConfigStore.getState().isReady) {
    return Promise.resolve();
  }

  if (bootPromise) {
    return bootPromise;
  }

  const promise = (async () => {
    const metadata = await dexie.cacheMetadata.get(CACHE_KEY);
    const hasValidCache = metadata?.cacheVersion === CACHE_VERSION;

    if (hasValidCache) {
      await useConfigStore.getState().loadConfigsFromDexie();
      void refreshDexieConfig().catch((error) => logger.error(error, "Failed to refresh UI config"));
      return;
    }

    await refreshDexieConfig();
  })();

  bootPromise = promise;
  void promise.then(
    () => {
      if (bootPromise === promise) {
        bootPromise = null;
      }
    },
    () => {
      if (bootPromise === promise) {
        bootPromise = null;
      }
    },
  );

  return promise;
}
